import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { WordOnlineViewer } from '@/components/WordOnlineViewer';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { FileText, Upload, Maximize2, Minimize2, X, Plus, Edit3, Eye, Loader2, Download, ArrowLeft, FolderOpen, Calendar, LayoutTemplate, Layout, ChevronDown, Package, FileDown, FileType, Wand2, CheckCircle } from 'lucide-react';
import JSZip from 'jszip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { TailoredDocumentService, TailoredDocumentSession } from '@/services/tailoredDocumentService';
import { DocumentEditingView } from '@/components/DocumentEditingView';
interface DocumentTab {
  id: string;
  fileName: string;
  fileUrl: string;
  uploadedFile?: File;
  isEditing?: boolean;
  resumeId?: string; // Add resume ID to track source resume
  resumeJson?: any; // Add resume JSON data for editing
  documentData?: any; // Add document data for context
  tailoringSessionId?: string; // Add tailoring session ID
  tailoredDocumentId?: string; // Add tailored document ID
  documentType?: string; // Add document type (resume, cover_letter, etc.)
  originalContent?: string; // Add original plain text content from database
  // Performance optimization flags
  rawJsonData?: any; // Store raw unprocessed JSON
  isDataPrepared?: boolean; // Flag to track if data is ready for editing
  isPreparingData?: boolean; // Flag to show data preparation loading
}
// Valid Docmosis template ids that pair with the dropdown on the results page.
// Keep this list in sync with `DOCMOSIS_TEMPLATE_OPTIONS` in ResumeResultsTemplated.
type DocmosisTemplateId =
  | 'template1' | 'template2' | 'template3'
  | 'template4' | 'template5' | 'template6'
  | 'template7' | 'template8' | 'template9'
  | 'rdr-blue-theme-v3.docx' | 'rdr-blue-theme-v3-burgundy.docx' | 'rdr-blue-theme-v3-forest.docx'
  | 'rdr-blue-theme-v3-teal.docx' | 'rdr-blue-theme-v3-plum.docx' | 'rdr-blue-theme-v3-black.docx'
  | 'original-format';

const VALID_TEMPLATE_IDS: readonly DocmosisTemplateId[] = [
  'template1', 'template2', 'template3',
  'template4', 'template5', 'template6',
  'template7', 'template8', 'template9',
  'rdr-blue-theme-v3.docx', 'rdr-blue-theme-v3-burgundy.docx', 'rdr-blue-theme-v3-forest.docx',
  'rdr-blue-theme-v3-teal.docx', 'rdr-blue-theme-v3-plum.docx', 'rdr-blue-theme-v3-black.docx',
  'original-format',
] as const;

const DOCMOSIS_TEMPLATE_OPTIONS: Array<{
  id: DocmosisTemplateId;
  label: string;
  subtitle: string;
}> = [
  { id: 'rdr-blue-theme-v3.docx', label: 'RDR Blue (Default)', subtitle: 'Navy, built from the approved RDR reference resume' },
  { id: 'rdr-blue-theme-v3-burgundy.docx', label: 'RDR Burgundy', subtitle: 'Same RDR design, burgundy bars and banner' },
  { id: 'rdr-blue-theme-v3-forest.docx', label: 'RDR Forest Green', subtitle: 'Same RDR design, forest green bars and banner' },
  { id: 'rdr-blue-theme-v3-teal.docx', label: 'RDR Teal', subtitle: 'Same RDR design, teal bars and banner' },
  { id: 'rdr-blue-theme-v3-plum.docx', label: 'RDR Plum', subtitle: 'Same RDR design, plum bars and banner' },
  { id: 'rdr-blue-theme-v3-black.docx', label: 'RDR Black', subtitle: 'Same RDR design, black bars and banner' },
  { id: 'original-format', label: 'Original Format (your design)', subtitle: 'Your own resume design, rebuilt with the new content' },
];

const docmosisTemplateLabel = (id: DocmosisTemplateId): string =>
  DOCMOSIS_TEMPLATE_OPTIONS.find((o) => o.id === id)?.label ?? id;

const WordDocumentViewer = () => {
  const {
    toast
  } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Template the editor renders documents in. Defaults to the new app-wide
  // default (template3 — Professional 2026 v8) but honors whatever template
  // was selected on the previous results page via navigation state, so opening
  // the Full Editor preserves the user's template choice.
  const incomingTemplateId = (location.state as any)?.docmosisTemplateId;
  const initialTemplateId: DocmosisTemplateId = VALID_TEMPLATE_IDS.includes(incomingTemplateId)
    ? incomingTemplateId
    : 'rdr-blue-theme-v3.docx';
  const [activeTemplateId, setActiveTemplateId] = useState<DocmosisTemplateId>(initialTemplateId);
  // The client's own Word file (from the results page). "Original Format"
  // rebuilds the resume in that file's design, so every render sends it along.
  const originalDocxUrl: string | undefined = (location.state as any)?.originalDocxUrl || undefined;
  const designExtra = (tid: string) => (tid === 'original-format' && originalDocxUrl ? { originalDocxUrl } : {});
  const [tabs, setTabs] = useState<DocumentTab[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const hasGeneratedFromState = useRef(false); // Track if we've already generated from initial state
  const lastBatchKeyRef = useRef<string | null>(null); // Track last multi-doc batch opened
  // New state for document library
  const [tailoredSessions, setTailoredSessions] = useState<TailoredDocumentSession[]>([]);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  // Performance optimization - data preparation
  const [preparingDataFor, setPreparingDataFor] = useState<string | null>(null);

  // Download states
  const [isDownloading, setIsDownloading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  // AI Editing states
  const [isEditingMode, setIsEditingMode] = useState(false);

  // Performance optimization - data preprocessing function
  const prepareResumeData = useCallback((rawData: any): any => {
    if (!rawData) return null;

    // Deep clone to avoid mutations
    const processedData = JSON.parse(JSON.stringify(rawData));

    // Limit array sizes to prevent UI freezing
    const limitArraySize = (arr: any[], maxSize: number = 50) => {
      return Array.isArray(arr) ? arr.slice(0, maxSize) : arr;
    };

    // Process work experience
    if (processedData.Experience && Array.isArray(processedData.Experience)) {
      processedData.Experience = limitArraySize(processedData.Experience, 10);
      processedData.Experience.forEach((exp: any) => {
        if (exp.bullets && Array.isArray(exp.bullets)) {
          exp.bullets = limitArraySize(exp.bullets, 15);
        }
      });
    }

    // Process education
    if (processedData.Education && Array.isArray(processedData.Education)) {
      processedData.Education = limitArraySize(processedData.Education, 5);
    }

    // Process skills
    if (processedData.Skills && Array.isArray(processedData.Skills)) {
      processedData.Skills = limitArraySize(processedData.Skills, 30);
    }

    // Process any other large arrays
    Object.keys(processedData).forEach(key => {
      if (Array.isArray(processedData[key]) && processedData[key].length > 100) {
        console.log(`Limiting ${key} array from ${processedData[key].length} to 50 items`);
        processedData[key] = limitArraySize(processedData[key], 50);
      }
    });
    return processedData;
  }, []);

  const normalizeDocumentType = (type?: string): string => (type || '').toLowerCase().replace(/-/g, '_');

  const isLetterDocumentType = (type?: string): boolean => {
    const normalized = normalizeDocumentType(type);
    return ['cover_letter', 'thank_you_letter', 'outreach_letter'].includes(normalized);
  };

  const isLinkedDocumentType = (type?: string): boolean => {
    const normalized = normalizeDocumentType(type);
    return ['linkedin_profile', 'executive_bio'].includes(normalized);
  };

  const getDocumentTypeLabel = (type?: string): string => {
    switch (normalizeDocumentType(type)) {
      case 'cover_letter':
        return 'Cover Letter';
      case 'thank_you_letter':
        return 'Thank You Letter';
      case 'outreach_letter':
        return 'Outreach Letter';
      case 'linkedin_profile':
        return 'LinkedIn Profile';
      case 'executive_bio':
        return 'Executive Bio';
      default:
        return 'Resume';
    }
  };

  const getTextValue = (value: any): string => {
    if (typeof value === 'string') return value.trim();

    if (Array.isArray(value)) {
      return value
        .map(item => getTextValue(item))
        .filter(Boolean)
        .join('\n\n')
        .trim();
    }

    if (value && typeof value === 'object') {
      if (typeof value.body === 'string') return value.body.trim();
      if (typeof value.Body === 'string') return value.Body.trim();
      if (typeof value.content === 'string') return value.content.trim();
      if (typeof value.Content === 'string') return value.Content.trim();
      if (typeof value.description === 'string') return value.description.trim();
      if (typeof value.Description === 'string') return value.Description.trim();
    }

    return '';
  };

  const extractLetterBody = (data: any, type?: string): string => {
    const normalized = normalizeDocumentType(type);

    // Also check inside legacy letters[0] wrapper
    const legacy = Array.isArray(data?.letters) ? data.letters[0] : null;

    const specificCandidates = normalized === 'cover_letter'
      ? [data?.coverLetterBody, data?.cover_letter_body, data?.CoverLetterBody, legacy?.coverLetterBody]
      : normalized === 'thank_you_letter'
        ? [data?.thankYouLetterBody, data?.thank_you_letter_body, data?.ThankYouLetterBody, data?.thankYouBody, legacy?.thankYouLetterBody]
        : [data?.outreachLetterBody, data?.outreach_letter_body, data?.OutreachLetterBody, data?.outreachBody, legacy?.outreachLetterBody];

    const genericCandidates = [
      ...specificCandidates,
      data?.Body,
      data?.body,
      data?.content,
      data?.Content,
      data?.text,
      data?.Text,
      data?.letter_body,
      data?.letterBody,
      legacy?.Body,
      legacy?.body,
    ];

    for (const candidate of genericCandidates) {
      const text = getTextValue(candidate);
      if (text) return text;
    }

    const paragraphText = [
      data?.Paragraph1 ?? legacy?.Paragraph1,
      data?.Paragraph2 ?? legacy?.Paragraph2,
      data?.Paragraph3 ?? legacy?.Paragraph3,
      data?.Paragraph4 ?? legacy?.Paragraph4,
    ]
      .map(value => getTextValue(value))
      .filter(Boolean)
      .join('\n\n');

    return paragraphText.trim();
  };

  const buildBulkLetterRequest = (sourceData: any, type?: string) => {
    const normalized = normalizeDocumentType(type);
    const headerInfo = sourceData?.headerInfo || sourceData?.header || {};
    const personalInfo = sourceData?.personal_information || sourceData?.PersonalInformation || {};
    const body = extractLetterBody(sourceData, normalized);

    return {
      letters: [{
        headerInfo: {
          fullName: getTextValue(headerInfo.fullName) || getTextValue(personalInfo.full_name || personalInfo.FullName) || getTextValue(sourceData?.Name || sourceData?.name) || 'Unknown',
          phoneNumber: getTextValue(headerInfo.phoneNumber) || getTextValue(personalInfo.phone || personalInfo.Phone) || getTextValue(sourceData?.Phone || sourceData?.phone),
          email: getTextValue(headerInfo.email) || getTextValue(personalInfo.email || personalInfo.Email) || getTextValue(sourceData?.Email || sourceData?.email),
          cityST: getTextValue(headerInfo.cityST) || getTextValue(personalInfo.location || personalInfo.Location) || getTextValue(sourceData?.Location || sourceData?.City),
          linkedin: getTextValue(headerInfo.linkedin) || getTextValue(personalInfo.linkedin || personalInfo.LinkedIn) || getTextValue(sourceData?.LinkedIn || sourceData?.linkedin),
          companyName: getTextValue(headerInfo.companyName) || getTextValue(sourceData?.CompanyName || sourceData?.company || sourceData?.companyName),
          recipientName: getTextValue(headerInfo.recipientName) || getTextValue(sourceData?.RecipientName || sourceData?.recipientName),
          jobTitle: getTextValue(headerInfo.jobTitle) || getTextValue(sourceData?.JobTitle || sourceData?.jobTitle || sourceData?.position),
        },
        coverLetterBody: normalized === 'cover_letter' ? body : undefined,
        thankYouLetterBody: normalized === 'thank_you_letter' ? body : undefined,
        outreachLetterBody: normalized === 'outreach_letter' ? body : undefined,
      }],
      letterTypes: {
        coverLetter: normalized === 'cover_letter',
        thankYouLetter: normalized === 'thank_you_letter',
        outreachLetter: normalized === 'outreach_letter',
      },
    };
  };

  // Load tailored document sessions from the library
  const loadTailoredSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const sessions = await TailoredDocumentService.getTailoredDocumentSessions();
      setTailoredSessions(sessions);
    } catch (error) {
      console.error('Error loading tailored sessions:', error);
      toast({
        title: "Error Loading Library",
        description: "Failed to load your tailored documents library",
        variant: "destructive"
      });
    } finally {
      setIsLoadingSessions(false);
    }
  }, [toast]);

  // Note: Split view now supports both JSON documents and regular Word docs

  // Load selected session documents into tabs (optimized)
  const loadSessionDocuments = useCallback(async (session: TailoredDocumentSession) => {
    console.log('Loading documents from session:', session.session_id);
    setIsLibraryOpen(false);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      toast({
        title: "Loading Documents",
        description: `Loading ${session.documents.length} document(s) from ${session.job_title} at ${session.company_name}...`
      });
      let documentsGenerated = 0;
      const totalDocuments = session.documents.length;

      // Load JSON data and create editor tabs FIRST, then optionally generate Word documents
      for (const [index, document] of session.documents.entries()) {
        // Get JSON data from tailored_documents.document_json_data field (not resumejs table)
        const jsonData = document.document_json_data;
        if (!jsonData) {
          console.warn(`No JSON data found for document ${document.id}`);
          toast({
            title: "Missing Document Data",
            description: `${document.document_type} JSON data not found`,
            variant: "destructive"
          });
          continue; // Skip this document and continue with next
        }
        let rawData;
        try {
          // Parse JSON data if it's a string
          if (typeof jsonData === 'string') {
            rawData = JSON.parse(jsonData);
          } else {
            rawData = jsonData;
          }
        } catch (parseError) {
          console.error(`Failed to parse JSON for ${document.document_type}:`, parseError);
          toast({
            title: "JSON Parse Error",
            description: `Invalid JSON data for ${document.document_type}`,
            variant: "destructive"
          });
          continue;
        }
        console.log(`Document ${index + 1}: Preparing JSON data for immediate editing...`);

        // Prepare data immediately for library documents since they're already structured
        const processedData = prepareResumeData(rawData);
        console.log(`Document ${index + 1}: Data prepared successfully, ready for editing`);

        // Create tab with processed data ready for editing
        const timestamp = Date.now() + index;
        const newTabId = `tab-${timestamp}`;
        const fileName = `${document.document_type}_${document.job_title}_${document.company_name}.docx`.replace(/[^a-zA-Z0-9._-]/g, '_');
        const newTab: DocumentTab = {
          id: newTabId,
          fileName: fileName,
          fileUrl: '',
          // Word document will be generated automatically
          rawJsonData: rawData,
          // Keep raw data for reference
          resumeJson: processedData,
          // Store processed data for immediate editing
          documentData: processedData,
          // Store as document data too
          tailoringSessionId: document.tailoring_session_id,
          tailoredDocumentId: document.id,
          documentType: document.document_type,
          // Store the plain text content from the database
          originalContent: document.content,
          isEditing: false,
          // Start in viewer mode
          isDataPrepared: true,
          // Data is prepared and ready
          isPreparingData: false // Not currently preparing
        };

        // Add tab immediately with prepared data
        setTabs(prev => [...prev, newTab]);

        // Set first tab as active
        if (index === 0) {
          setActiveTab(newTabId);
        }
        console.log(`Document ${index + 1}: Generating Word document automatically...`);

        // Automatically generate Word document for immediate viewing
        try {
          // Choose the right endpoint based on document type
          let endpoint = 'generate-bulk-resumes';
          let requestBody: any = processedData;
          const docType = normalizeDocumentType(document.document_type);

          if (isLetterDocumentType(docType)) {
            endpoint = 'generate-bulk-letters';
            requestBody = { ...buildBulkLetterRequest(processedData, docType), templateId: activeTemplateId, ...designExtra(activeTemplateId) };
          } else if (isLinkedDocumentType(docType)) {
            // Use VectorShift-only LinkedIn document generation (no OpenAI)
            endpoint = 'generate-linkedin-document';
            requestBody = rawData; // Pass RAW VectorShift data directly (NOT processedData)
          } else {
            // Resume — add templateId
            requestBody = { ...processedData, templateId: activeTemplateId, ...designExtra(activeTemplateId) };
          }

          console.log(`[loadSessionDocuments] Document type: ${docType}, using endpoint: ${endpoint}`);

          const {
            data: wordData,
            error: wordError
          } = await supabase.functions.invoke(endpoint, {
            body: requestBody
          });
          if (wordError) throw wordError;
          if (!wordData.success || !wordData.files?.length) throw new Error(`No files generated for ${docType}`);

          // Convert base64 to blob
          const binaryString = atob(wordData.files[0].data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          });

          // Upload the generated file to storage
          const wordFileName = wordData.files[0].filename;
          const safeFileName = wordFileName.replace(/[^a-zA-Z0-9._-]/g, '_');
          const wordTimestamp = Date.now();
          const storagePath = `generated_${wordTimestamp}_${safeFileName}`;
          const {
            data: uploadData,
            error: uploadError
          } = await supabase.storage.from('word-documents').upload(storagePath, blob, {
            cacheControl: '3600',
            upsert: false
          });
          if (uploadError) throw uploadError;

          // Get the public URL
          const {
            data: urlData
          } = supabase.storage.from('word-documents').getPublicUrl(storagePath);
          if (urlData?.publicUrl) {
            // Update the tab with the Word document URL
            setTabs(prev => prev.map(tab => tab.id === newTabId ? {
              ...tab,
              fileUrl: urlData.publicUrl
            } : tab));
            console.log(`Document ${index + 1}: Word document generated and ready for viewing`);
          }
        } catch (wordError) {
          console.error(`Failed to generate Word document for ${document.document_type}:`, wordError);
          // Don't show error toast here since the editor functionality still works
          console.log(`Document ${index + 1}: Word generation failed, but editor is still available`);
        }
        documentsGenerated++;
      }

      // Show completion message
      if (documentsGenerated > 0) {
        toast({
          title: "Documents Loaded",
          description: `Successfully loaded ${documentsGenerated} document(s) from ${session.job_title}`
        });
      }
    } catch (error) {
      console.error('Error loading session documents:', error);
      toast({
        title: "Error Loading Documents",
        description: "Failed to load documents from the selected session",
        variant: "destructive"
      });
    }
  }, [toast]);

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Convert resume JSON to readable text format
  // Convert resume JSON to readable text format
  const convertResumeJsonToText = (resumeJson: any): string => {
    if (!resumeJson) return '';
    
    let text = '';
    
    // Personal Information - Handle both formats
    let hasPersonalInfo = false;
    if (resumeJson.PersonalInformation) {
      const info = resumeJson.PersonalInformation;
      if (info.FullName) text += `${info.FullName}\n`;
      if (info.Email) text += `${info.Email}\n`;
      if (info.PhoneNumber) text += `${info.PhoneNumber}\n`;
      if (info.Address) text += `${info.Address}\n`;
      if (info.LinkedIn) text += `LinkedIn: ${info.LinkedIn}\n`;
      hasPersonalInfo = true;
    } else {
      // Handle direct format (Name, Email, Phone, etc.)
      if (resumeJson.Name) text += `${resumeJson.Name}\n`;
      if (resumeJson.Email) text += `${resumeJson.Email}\n`;
      if (resumeJson.Phone) text += `${resumeJson.Phone}\n`;
      if (resumeJson.City || resumeJson.State) {
        const location = [resumeJson.City, resumeJson.State].filter(Boolean).join(', ');
        if (location) text += `${location}\n`;
      }
      if (resumeJson.LinkedIn) text += `LinkedIn: ${resumeJson.LinkedIn}\n`;
      hasPersonalInfo = resumeJson.Name || resumeJson.Email || resumeJson.Phone;
    }
    if (hasPersonalInfo) text += '\n';
    
    // Professional Summary - Handle both formats
    const summary = resumeJson.ProfessionalSummary || resumeJson.Summary;
    if (summary) {
      text += 'PROFESSIONAL SUMMARY\n';
      text += `${summary}\n\n`;
    }
    
    // Work Experience - Handle both formats
    const experience = resumeJson.Experience || resumeJson.ProfessionalExperience;
    if (experience && Array.isArray(experience) && experience.length > 0) {
      text += 'WORK EXPERIENCE\n';
      experience.forEach((exp: any) => {
        const jobTitle = exp.JobTitle || exp.job_title;
        const company = exp.Company || exp.company;
        const startDate = exp.StartDate || exp.start_date;
        const endDate = exp.EndDate || exp.end_date;
        const location = exp.Location || exp.location;
        const descriptions = exp.bullets || exp.Descriptions || [];
        
        if (jobTitle) text += `${jobTitle}\n`;
        if (company) text += `${company}\n`;
        if (startDate || endDate) {
          text += `${startDate || 'Start'} - ${endDate || 'Present'}\n`;
        }
        if (location) text += `${location}\n`;
        
        if (Array.isArray(descriptions) && descriptions.length > 0) {
          descriptions.forEach((desc: any) => {
            const description = typeof desc === 'string' ? desc : desc.Description;
            if (description) text += `• ${description}\n`;
          });
        }
        text += '\n';
      });
    }
    
    // Education - Handle both formats
    if (resumeJson.Education && Array.isArray(resumeJson.Education) && resumeJson.Education.length > 0) {
      text += 'EDUCATION\n';
      resumeJson.Education.forEach((edu: any) => {
        const degree = edu.Degree || edu.College_Degree_Name;
        const institution = edu.Institution || edu.College_Name;
        const graduationDate = edu.GraduationDate || edu.Year_Earned;
        const gpa = edu.GPA;
        
        if (degree) text += `${degree}\n`;
        if (institution) text += `${institution}\n`;
        if (graduationDate) text += `${graduationDate}\n`;
        if (gpa) text += `GPA: ${gpa}\n`;
        text += '\n';
      });
    }
    
    // Skills - Handle both formats
    const skills = resumeJson.Skills;
    if (skills && Array.isArray(skills) && skills.length > 0) {
      text += 'SKILLS\n';
      const skillsList = skills.map(skill => 
        typeof skill === 'string' ? skill : skill.Skill
      ).filter(Boolean);
      if (skillsList.length > 0) {
        text += skillsList.join(', ') + '\n\n';
      }
    }
    
    // Technical Skills
    const techSkills = resumeJson.TechnicalSkills;
    if (techSkills && Array.isArray(techSkills) && techSkills.length > 0) {
      text += 'TECHNICAL SKILLS\n';
      const techSkillsList = techSkills.map(skill => 
        typeof skill === 'string' ? skill : skill.Skill
      ).filter(Boolean);
      if (techSkillsList.length > 0) {
        text += techSkillsList.join(', ') + '\n\n';
      }
    }
    
    // Career Highlights
    const careerHighlights = resumeJson.CareerHighlights;
    if (careerHighlights && Array.isArray(careerHighlights) && careerHighlights.length > 0) {
      text += 'CAREER HIGHLIGHTS\n';
      careerHighlights.forEach((highlight: any) => {
        if (highlight.chtitle) text += `${highlight.chtitle}\n`;
        if (highlight.chdescription) text += `${highlight.chdescription}\n\n`;
      });
    }
    
    // Certifications
    const certifications = resumeJson.Certifications || resumeJson.CertificationsAndLicenses;
    if (certifications && Array.isArray(certifications) && certifications.length > 0) {
      text += 'CERTIFICATIONS\n';
      certifications.forEach((cert: any) => {
        if (typeof cert === 'string') {
          text += `• ${cert}\n`;
        } else if (cert.Name || cert.Certification_or_License_Name) {
          const name = cert.Name || cert.Certification_or_License_Name;
          const issuer = cert.Issuer || cert.Institution_Organization;
          const date = cert.Date || cert.Year;
          text += `• ${name}`;
          if (issuer) text += ` - ${issuer}`;
          if (date) text += ` (${date})`;
          text += '\n';
        }
      });
      text += '\n';
    }
    
    return text.trim();
  };
  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'cover_letter':
        return '📝';
      case 'thank_you_letter':
        return '💌';
      case 'executive_bio':
        return '👤';
      case 'linkedin_profile':
        return '💼';
      case 'outreach_letter':
        return '📨';
      default:
        return '📄';
    }
  };

  // Generate Word document from JSON data (resume, cover letter, etc.)
  // NOTE: Removed isGenerating check to allow multiple documents to be generated in sequence
  const generateWordFromJson = async (documentData: any, tailoringSessionId?: string, tailoredDocumentId?: string, documentType?: string, existingFileUrl?: string, existingFileName?: string): Promise<DocumentTab | null> => {
    try {
      console.log('Generating Word document from JSON:', documentData);
      console.log('Document type:', documentType);

      // Parse the JSON data if it's wrapped in a _type object
      let parsedData = documentData;
      if (documentData?._type === 'String' && documentData?.value) {
        console.log('Unwrapping JSON string from _type wrapper');
        parsedData = JSON.parse(documentData.value);
        console.log('Parsed data:', parsedData);
      }

      // The results page already has the final, fitted file: open it as-is.
      // Re-rendering from the JSON loses the layout fixes made in the file.
      if (existingFileUrl) {
        const timestamp = Date.now();
        const newTab: DocumentTab = {
          id: `tab-${timestamp}`,
          fileName: existingFileName || decodeURIComponent(existingFileUrl.split('/').pop() || 'resume.docx'),
          fileUrl: existingFileUrl,
          resumeJson: parsedData,
          documentData: parsedData,
          tailoringSessionId,
          tailoredDocumentId,
          documentType: documentType || 'resume',
          isEditing: false,
        };
        setTabs(prev => [...prev, newTab]);
        return newTab;
      }

      // Choose the right endpoint based on document type
      let endpoint = 'generate-bulk-resumes'; // Default for resume
      let requestBody: any = { ...parsedData, templateId: activeTemplateId, ...designExtra(activeTemplateId) };

      // Route letter types to bulk letters endpoint
      const normalizedDocType = normalizeDocumentType(documentType);
      if (isLetterDocumentType(normalizedDocType)) {
        endpoint = 'generate-bulk-letters';
        requestBody = { ...buildBulkLetterRequest(parsedData, normalizedDocType), templateId: activeTemplateId, ...designExtra(activeTemplateId) };
      } else if (isLinkedDocumentType(normalizedDocType)) {
        // Use VectorShift-only LinkedIn document generation (no OpenAI)
        endpoint = 'generate-linkedin-document';
        requestBody = parsedData; // Pass VectorShift data directly
      }

      const {
        data,
        error
      } = await supabase.functions.invoke(endpoint, {
        body: requestBody
      });
      if (error) throw error;
      if (!data.success || !data.files?.length) throw new Error(`No files generated for ${normalizeDocumentType(documentType)}`);

      // Convert base64 to blob
      const binaryString = atob(data.files[0].data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      // Upload the generated file to storage
      const fileName = data.files[0].filename;
      const timestamp = Date.now();
      const storagePath = `generated_${timestamp}_${fileName}`;
      const {
        data: uploadData,
        error: uploadError
      } = await supabase.storage.from('word-documents').upload(storagePath, blob, {
        cacheControl: '3600',
        upsert: false
      });
      if (uploadError) throw uploadError;

      // Get the public URL
      const {
        data: urlData
      } = supabase.storage.from('word-documents').getPublicUrl(storagePath);
      if (urlData?.publicUrl) {
        // Create new tab with the generated document and store the PARSED JSON data
        const newTabId = `tab-${timestamp}`;
        const newTab: DocumentTab = {
          id: newTabId,
          fileName: fileName,
          fileUrl: urlData.publicUrl,
          resumeJson: parsedData,
          // Store the parsed JSON data for editing
          documentData: parsedData,
          // Store as document data too
          tailoringSessionId: tailoringSessionId,
          // Store tailoring session ID
          tailoredDocumentId: tailoredDocumentId,
          // Store tailored document ID
          documentType: documentType || 'resume',
          // Store document type
          isEditing: false // Start in viewer mode when JSON data is available
        };
        setTabs(prev => [...prev, newTab]);
        const docTypeLabel = documentType === 'cover_letter' ? 'Cover Letter' : 'Resume';
        toast({
          title: "Document Generated Successfully!",
          description: `${docTypeLabel} is ready for viewing`
        });
        return newTab;
      } else {
        throw new Error('Failed to get public URL');
      }
    } catch (error: any) {
      console.error('Error generating Word document:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate Word document",
        variant: "destructive"
      });
      return null;
    }
  };

  // Handle generated files from processing page or JSON data
  useEffect(() => {
    // Scroll to top when component mounts with new documents
    window.scrollTo(0, 0);
    const state = location.state as any;

    // Handle JSON data (single doc) - only once per component mount
    if (state?.resumeJson && tabs.length === 0 && !hasGeneratedFromState.current) {
      console.log('Found JSON document data, generating Word document...');
      console.log('Document type:', state?.documentType);
      console.log('Tailoring session ID:', state?.tailoringSessionId);
      console.log('Tailored document ID:', state?.tailoredDocumentId);
      hasGeneratedFromState.current = true; // Mark as generated
      setIsGenerating(true);
      generateWordFromJson(state.resumeJson, state?.tailoringSessionId, state?.tailoredDocumentId, state?.documentType)
        .finally(() => setIsGenerating(false));
      return;
    }

    // Handle multiple documents from tailored document detail page
    // IMPORTANT: Allow re-opening in the same viewer instance by detecting a new batch key.
    if (state?.multipleDocuments && Array.isArray(state.multipleDocuments) && !isGenerating) {
      const batchKey = (() => {
        try {
          return JSON.stringify(state.multipleDocuments.map((d: any) => ({
            tailoredDocumentId: d.tailoredDocumentId,
            documentType: d.documentType,
            title: d.title
          })));
        } catch {
          return String(Date.now());
        }
      })();

      const lastBatchKey = lastBatchKeyRef.current;

      if (lastBatchKey !== batchKey) {
        console.log('Found multiple JSON documents, generating Word documents...');
        console.log('Number of documents:', state.multipleDocuments.length);

        // Reset tabs so a new batch can load even if tabs already exist
        setTabs([]);
        setActiveTab('');

        // Store batch key
        lastBatchKeyRef.current = batchKey;

        setIsGenerating(true);

        const generateAllDocuments = async () => {
          let firstTabId: string | null = null;

          for (let index = 0; index < state.multipleDocuments.length; index++) {
            const docData = state.multipleDocuments[index];
            console.log(`Generating document ${index + 1}/${state.multipleDocuments.length}: ${docData.documentType}`);

            const tab = await generateWordFromJson(
              docData.resumeJson,
              docData.tailoringSessionId,
              docData.tailoredDocumentId,
              docData.documentType,
              docData.existingFileUrl,
              docData.filename
            );

            if (index === 0 && tab) {
              firstTabId = tab.id;
              setActiveTab(tab.id);
            }
          }

          setIsGenerating(false);

          toast({
            title: "All Documents Ready!",
            description: `Successfully generated ${state.multipleDocuments.length} document(s) for viewing.`,
          });

          // If first tab wasn't set during generation for any reason, set it now
          if (!firstTabId && state.multipleDocuments.length > 0) {
            setActiveTab(prev => prev || tabs[0]?.id || '');
          }
        };

        generateAllDocuments();
        return;
      }
    }

    // Handle bulk letters from bulk generator
    if (state?.bulkLetters && Array.isArray(state.bulkLetters) && !hasGeneratedFromState.current) {
      console.log('Loading bulk letters from generator:', state.bulkLetters);
      hasGeneratedFromState.current = true;

      const bulkTabs = state.bulkLetters.map((file: any, index: number) => {
        const timestamp = Date.now() + index;
        return {
          id: `bulk-tab-${timestamp}`,
          fileName: file.filename,
          fileUrl: file.fileUrl,
          documentType: file.documentType,
        };
      });

      setTabs(bulkTabs);
      if (bulkTabs.length > 0) {
        setActiveTab(bulkTabs[0].id);
      }

      toast({
        title: "Bulk Letters Loaded",
        description: `${bulkTabs.length} letters loaded and ready to view`,
      });
      return;
    }

    // Handle pre-generated files
    if (state?.generatedFiles && Array.isArray(state.generatedFiles)) {
      console.log('Loading generated files:', state.generatedFiles);
      console.log('Resume ID:', state?.resumeId);
      const newTabs: DocumentTab[] = state.generatedFiles.map((fileUrl: string, index: number) => {
        const filename = state?.fileName || extractFilenameFromUrl(fileUrl);
        return {
          id: `tab-${Date.now()}-${index}`,
          fileName: filename,
          fileUrl: fileUrl,
          resumeId: state?.resumeId // Store resume ID
        };
      });
      setTabs(newTabs);
      if (newTabs.length > 0) {
        setActiveTab(newTabs[0].id);
      }
      toast({
        title: "Documents Generated Successfully!",
        description: `${newTabs.length} tailored document(s) are ready for review.`
      });
    }
  }, [location.state, toast, isGenerating, tabs.length]);

  // Handle URL parameters to auto-load documents (existing functionality)
  useEffect(() => {
    console.log('=== WORD VIEWER DEBUG ===');
    console.log('Current URL:', window.location.href);
    console.log('Search params:', window.location.search);
    const urlParams = new URLSearchParams(window.location.search);
    const documentUrl = urlParams.get('document');
    const filename = urlParams.get('filename');
    const jsonResumeParam = urlParams.get('json');
    console.log('Document URL from params:', documentUrl);
    console.log('Filename from params:', filename);
    console.log('JSON Resume param:', jsonResumeParam);
    console.log('Current tabs length:', tabs.length);

    // Handle JSON data from URL parameter - only once per component mount
    if (jsonResumeParam && tabs.length === 0 && !isGenerating && !hasGeneratedFromState.current) {
      try {
        const documentData = JSON.parse(decodeURIComponent(jsonResumeParam));
        console.log('Found JSON document data in URL, generating Word document...');
        hasGeneratedFromState.current = true; // Mark as generated
        generateWordFromJson(documentData);
        return;
      } catch (error) {
        console.error('Error parsing JSON resume data from URL:', error);
        toast({
          title: "Invalid Data",
          description: "Failed to parse resume data from URL",
          variant: "destructive"
        });
      }
    }
    if (documentUrl && filename && tabs.length === 0) {
      console.log('Creating new tab for document');
      // Auto-load document from URL parameters (only if no tabs from state)
      const newTabId = `tab-${Date.now()}`;
      const newTab: DocumentTab = {
        id: newTabId,
        fileName: decodeURIComponent(filename),
        fileUrl: decodeURIComponent(documentUrl)
      };
      setTabs([newTab]);
      setActiveTab(newTabId);
      toast({
        title: "Document Loaded",
        description: "Your document is ready for editing!"
      });
    } else {
      console.log('Document not loaded - missing params or tabs already exist');
    }
  }, [toast, tabs.length]);
  const extractFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'document.docx';
      return decodeURIComponent(filename);
    } catch (error) {
      console.error('Error extracting filename from URL:', error);
      return 'document.docx';
    }
  };

  // Performance optimized toggle edit mode
  const toggleEditMode = useCallback(async (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) {
      console.log('toggleEditMode: Tab not found for ID:', tabId);
      return;
    }
    console.log('toggleEditMode called for tab:', tabId);
    console.log('Tab state:', {
      isEditing: tab.isEditing,
      isDataPrepared: tab.isDataPrepared,
      hasResumeJson: !!tab.resumeJson,
      hasRawJsonData: !!tab.rawJsonData,
      hasFileUrl: !!tab.fileUrl,
      documentType: tab.documentType
    });

    // If we have prepared JSON data (from library), just toggle edit mode
    if (tab.resumeJson && tab.isDataPrepared) {
      console.log('Toggling edit mode with prepared data');
      setTabs(prev => prev.map(t => t.id === tabId ? {
        ...t,
        isEditing: !t.isEditing
      } : t));
      return;
    }

    // If switching to edit mode and data isn't prepared, prepare it first
    if (!tab.isEditing && !tab.isDataPrepared && tab.rawJsonData) {
      console.log('Preparing data for editing...');
      setPreparingDataFor(tabId);

      // Update tab to show preparing state
      setTabs(prev => prev.map(t => t.id === tabId ? {
        ...t,
        isPreparingData: true
      } : t));
      try {
        // Process data in a setTimeout to allow UI to update
        await new Promise(resolve => setTimeout(resolve, 100));
        const processedData = prepareResumeData(tab.rawJsonData);

        // Update tab with processed data
        setTabs(prev => prev.map(t => t.id === tabId ? {
          ...t,
          resumeJson: processedData,
          documentData: processedData,
          isDataPrepared: true,
          isPreparingData: false,
          isEditing: true
        } : t));
        toast({
          title: "Editor Ready",
          description: "Document data prepared for editing"
        });
      } catch (error) {
        console.error('Error preparing data:', error);
        toast({
          title: "Error",
          description: "Failed to prepare document for editing",
          variant: "destructive"
        });
        setTabs(prev => prev.map(t => t.id === tabId ? {
          ...t,
          isPreparingData: false
        } : t));
      } finally {
        setPreparingDataFor(null);
      }
    } else if (!tab.isEditing && !tab.rawJsonData && !tab.resumeJson) {
      // No JSON data available for editing
      console.log('No JSON data available for editing');
      toast({
        title: "Cannot Edit Document",
        description: "This document doesn't have editable JSON data. Only documents loaded from your tailored library can be edited.",
        variant: "destructive"
      });
    } else {
      // Simple toggle for already prepared data
      console.log('Simple toggle for prepared data');
      setTabs(prev => prev.map(tab => tab.id === tabId ? {
        ...tab,
        isEditing: !tab.isEditing
      } : tab));
    }
  }, [tabs, prepareResumeData, toast]);
  const addNewTab = () => {
    const newTabId = `tab-${Date.now()}`;
    const newTab: DocumentTab = {
      id: newTabId,
      fileName: 'New Document',
      fileUrl: ''
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTab(newTabId);
  };
  const closeTab = (tabId: string) => {
    setTabs(prev => {
      const newTabs = prev.filter(tab => tab.id !== tabId);
      if (activeTab === tabId && newTabs.length > 0) {
        setActiveTab(newTabs[0].id);
      } else if (newTabs.length === 0) {
        setActiveTab('');
      }
      return newTabs;
    });
  };
  const updateTab = (tabId: string, updates: Partial<DocumentTab>) => {
    setTabs(prev => prev.map(tab => tab.id === tabId ? {
      ...tab,
      ...updates
    } : tab));
  };

  // Tracks tab IDs currently being re-rendered with a new template so we can
  // gray out the matching pill in the dropdown while a swap is in flight.
  const [regeneratingTabIds, setRegeneratingTabIds] = useState<Set<string>>(new Set());

  /**
   * Regenerate the Word document for a single tab using an explicit JSON,
   * upload it to storage, and point the tab's preview iframe at the new URL.
   * Shared by the manual Save button, the AI auto-save, and the template
   * switcher so we never have multiple copies of this logic to keep in sync.
   */
  const regenerateAndAttach = useCallback(async (
    tabId: string,
    rawDocType: string | undefined,
    jsonData: any,
  ): Promise<void> => {
    const docType = normalizeDocumentType(rawDocType);
    let endpoint = 'generate-bulk-resumes';
    let requestBody: any = { ...jsonData, templateId: activeTemplateId, ...designExtra(activeTemplateId) };

    if (isLetterDocumentType(docType)) {
      endpoint = 'generate-bulk-letters';
      requestBody = { ...buildBulkLetterRequest(jsonData, docType), templateId: activeTemplateId, ...designExtra(activeTemplateId) };
    } else if (isLinkedDocumentType(docType)) {
      endpoint = 'generate-linkedin-document';
      requestBody = jsonData;
    }

    const { data: wordData, error: wordError } = await supabase.functions.invoke(endpoint, {
      body: requestBody,
    });
    if (wordError) throw wordError;
    if (!wordData?.success || !wordData?.files?.length) throw new Error('Failed to regenerate document');

    const binary = atob(wordData.files[0].data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const wordFileName = wordData.files[0].filename;
    const safe = wordFileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `generated_${Date.now()}_${safe}`;
    const { error: uploadErr } = await supabase.storage
      .from('word-documents')
      .upload(storagePath, blob, { cacheControl: '3600', upsert: false });
    if (uploadErr) throw uploadErr;

    const { data: urlData } = supabase.storage.from('word-documents').getPublicUrl(storagePath);
    if (urlData?.publicUrl) {
      // Updating fileUrl bumps WordOnlineViewer's refreshKey via its own
      // useEffect on documentUrl, so the iframe reloads and the user sees
      // the freshly-rendered document.
      updateTab(tabId, { fileUrl: urlData.publicUrl, fileName: wordFileName });
    }
  }, [activeTemplateId]);

  /**
   * Re-render every open tab in a new Docmosis template. Used by the
   * template-switch dropdown in the editor header. Tabs that don't carry
   * source JSON (e.g. plain file uploads with no `resumeJson`) are skipped
   * with a toast — they can't be re-templated without their source data.
   */
  const switchTemplateForAllTabs = useCallback(async (newTemplate: DocmosisTemplateId) => {
    if (newTemplate === activeTemplateId) return;
    setActiveTemplateId(newTemplate);
    if (tabs.length === 0) return;

    setIsGenerating(true);
    let regenerated = 0;
    let skipped = 0;
    const skippedNames: string[] = [];

    for (const tab of tabs) {
      const jsonData = tab.resumeJson || tab.documentData || tab.rawJsonData;
      if (!jsonData) {
        skipped++;
        if (tab.fileName) skippedNames.push(tab.fileName);
        continue;
      }

      const docType = normalizeDocumentType(tab.documentType);
      if (isLinkedDocumentType(docType)) {
        // LinkedIn uses its own fixed template; no resume-pair to switch.
        skipped++;
        continue;
      }

      try {
        setRegeneratingTabIds(prev => new Set(prev).add(tab.id));

        let endpoint = 'generate-bulk-resumes';
        let requestBody: any = { ...jsonData, templateId: newTemplate, ...designExtra(newTemplate) };
        if (isLetterDocumentType(docType)) {
          endpoint = 'generate-bulk-letters';
          requestBody = { ...buildBulkLetterRequest(jsonData, docType), templateId: newTemplate, ...designExtra(newTemplate) };
        }

        const { data: wordData, error: wordError } = await supabase.functions.invoke(endpoint, {
          body: requestBody,
        });
        if (wordError) throw wordError;
        if (!wordData?.success || !wordData?.files?.length) throw new Error('No file returned');

        const binary = atob(wordData.files[0].data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });

        const fileNameOut = wordData.files[0].filename || tab.fileName;
        const safe = fileNameOut.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storagePath = `generated_${Date.now()}_${safe}`;
        const { error: uploadErr } = await supabase.storage
          .from('word-documents')
          .upload(storagePath, blob, { cacheControl: '3600', upsert: false });
        if (uploadErr) throw uploadErr;
        const { data: urlData } = supabase.storage.from('word-documents').getPublicUrl(storagePath);
        if (urlData?.publicUrl) {
          updateTab(tab.id, { fileUrl: urlData.publicUrl, fileName: fileNameOut });
          regenerated++;
        }
      } catch (err) {
        console.error(`[switchTemplate] Failed to regenerate ${tab.fileName}:`, err);
        skipped++;
        if (tab.fileName) skippedNames.push(tab.fileName);
      } finally {
        setRegeneratingTabIds(prev => {
          const next = new Set(prev);
          next.delete(tab.id);
          return next;
        });
      }
    }

    setIsGenerating(false);
    const label = docmosisTemplateLabel(newTemplate);
    if (regenerated > 0) {
      toast({
        title: `Switched to ${label}`,
        description: skipped > 0
          ? `Re-rendered ${regenerated} document(s). Skipped ${skipped} (no source data${skippedNames.length ? `: ${skippedNames.slice(0, 3).join(', ')}${skippedNames.length > 3 ? '…' : ''}` : ''}).`
          : `Re-rendered ${regenerated} document(s) in the new template.`,
      });
    } else {
      toast({
        title: 'Could not switch template',
        description: 'No documents had the source JSON needed to re-render. Try opening from the results page so the template can apply.',
        variant: 'destructive',
      });
    }
  }, [tabs, activeTemplateId, toast, normalizeDocumentType, isLetterDocumentType, isLinkedDocumentType, buildBulkLetterRequest]);

  const handleResumeUpdate = (tabId: string, newDocumentUrl: string, newFileName?: string) => {
    updateTab(tabId, {
      fileUrl: newDocumentUrl,
      fileName: newFileName || tabs.find(t => t.id === tabId)?.fileName || 'Updated Resume'
      // Keep editing mode - don't switch back automatically
      // isEditing: false  // Removed this line
    });
  };

  // Format document content for display in split view  
  const formatDocumentContent = (resumeJson: any, documentType?: string): string => {
    console.log('WordDocumentViewer: formatDocumentContent called with:', {
      resumeJson: resumeJson ? Object.keys(resumeJson) : null,
      documentType,
      hasData: !!resumeJson
    });
    if (!resumeJson) {
      console.log('WordDocumentViewer: No resumeJson provided');
      return 'No document content available. Please ensure the document has been properly loaded from the library.';
    }
    try {
      // Parse if it's a string
      if (typeof resumeJson === 'string') {
        resumeJson = JSON.parse(resumeJson);
      }
      let content = '';
      if (documentType === 'cover_letter') {
        // Enhanced cover letter formatting
        if (resumeJson.header || resumeJson.personal_information) {
          const header = resumeJson.header || `${resumeJson.personal_information?.full_name || resumeJson.Name || 'Your Name'}
${resumeJson.personal_information?.email || resumeJson.Email || ''}
${resumeJson.personal_information?.phone || resumeJson.Phone || ''}`;
          content += `${header}\n\n`;
        }
        if (resumeJson.date) {
          content += `${resumeJson.date}\n\n`;
        }
        if (resumeJson.employer_info) {
          content += `${resumeJson.employer_info}\n\n`;
        }
        if (resumeJson.salutation) {
          content += `${resumeJson.salutation}\n\n`;
        } else {
          content += 'Dear Hiring Manager,\n\n';
        }

        // Body paragraphs
        if (resumeJson.body && Array.isArray(resumeJson.body)) {
          resumeJson.body.forEach((paragraph: string) => {
            content += `${paragraph}\n\n`;
          });
        } else if (resumeJson.Paragraph1 || resumeJson.Paragraph2 || resumeJson.Paragraph3) {
          // Legacy format
          if (resumeJson.Paragraph1) content += `${resumeJson.Paragraph1}\n\n`;
          if (resumeJson.Paragraph2) content += `${resumeJson.Paragraph2}\n\n`;
          if (resumeJson.Paragraph3) content += `${resumeJson.Paragraph3}\n\n`;
          if (resumeJson.Paragraph4) content += `${resumeJson.Paragraph4}\n\n`;
        }
        if (resumeJson.closing) {
          content += `${resumeJson.closing}\n\n`;
        } else {
          content += 'Sincerely,\n\n';
        }
        if (resumeJson.signature) {
          content += resumeJson.signature;
        } else if (resumeJson.Name || resumeJson.personal_information?.full_name) {
          content += resumeJson.Name || resumeJson.personal_information.full_name;
        }
      } else {
        // Enhanced resume formatting
        const sections = [];

        // Personal Information
        if (resumeJson.personal_information) {
          const personal = resumeJson.personal_information;
          let header = personal.full_name || '';
          if (personal.email || personal.phone || personal.location) {
            header += '\n';
            const contact = [personal.email, personal.phone, personal.location].filter(Boolean).join(' | ');
            header += contact;
          }
          if (personal.linkedin) {
            header += `\n${personal.linkedin}`;
          }
          sections.push(header);
        } else if (resumeJson.PersonalInformation || resumeJson.Name) {
          // Legacy format
          const name = resumeJson.PersonalInformation?.FullName || resumeJson.Name || '';
          const email = resumeJson.PersonalInformation?.Email || resumeJson.Email || '';
          const phone = resumeJson.PersonalInformation?.Phone || resumeJson.Phone || '';
          const location = resumeJson.PersonalInformation?.Location || resumeJson.Location || '';
          let header = name;
          if (email || phone || location) {
            header += '\n' + [email, phone, location].filter(Boolean).join(' | ');
          }
          sections.push(header);
        }

        // Professional Summary
        if (resumeJson.professional_summary) {
          sections.push(`PROFESSIONAL SUMMARY\n${resumeJson.professional_summary}`);
        } else if (resumeJson.Summary || resumeJson.ProfessionalSummary) {
          sections.push(`PROFESSIONAL SUMMARY\n${resumeJson.Summary || resumeJson.ProfessionalSummary}`);
        }

        // Career Highlights (VectorShift schema)
        if (resumeJson.CareerHighlights && Array.isArray(resumeJson.CareerHighlights)) {
          const hSection = ['CAREER HIGHLIGHTS'];
          resumeJson.CareerHighlights.forEach((h: any) => {
            const title = (h?.chtitle || '').toString().trim();
            const desc = (h?.chdescription || '').toString().trim();
            if (title || desc) {
              if (title && desc) hSection.push(`${title}: ${desc}`);
              else hSection.push(title || desc);
            }
          });
          if (hSection.length > 1) sections.push(hSection.join('\n'));
        }

        // Professional Experience (VectorShift schema — different key name
        // from `Experience` used by the legacy branch below).
        if (resumeJson.ProfessionalExperience && Array.isArray(resumeJson.ProfessionalExperience)) {
          const expSection = ['PROFESSIONAL EXPERIENCE'];
          resumeJson.ProfessionalExperience.forEach((job: any) => {
            const titleBits = [
              job.job_title || job.JobTitle || job.Title || '',
              job.company || job.Company || '',
              job.location || job.Location || '',
              [job.start_date || job.StartDate, job.end_date || job.EndDate || 'Present']
                .filter(Boolean).join(' - '),
            ].filter(Boolean);
            expSection.push(titleBits.join(' | '));
            const descriptions = job.Descriptions || job.descriptions || job.Responsibilities || job.responsibilities;
            if (Array.isArray(descriptions)) {
              descriptions.forEach((d: any) => {
                const text = typeof d === 'string' ? d : (d?.Description || d?.description || '');
                if (text) expSection.push(`• ${text}`);
              });
            } else if (job.description) {
              expSection.push(`• ${job.description}`);
            }
            expSection.push('');
          });
          if (expSection.length > 1) sections.push(expSection.join('\n').trim());
        }

        // Work Experience (snake_case schema — only runs if PE wasn't found)
        else if (resumeJson.work_experience && Array.isArray(resumeJson.work_experience)) {
          const expSection = ['WORK EXPERIENCE'];
          resumeJson.work_experience.forEach((job: any) => {
            let jobLine = job.job_title || job.position || '';
            if (job.company) jobLine += ` | ${job.company}`;
            if (job.location) jobLine += ` | ${job.location}`;
            if (job.start_date || job.end_date) {
              const dates = [job.start_date, job.end_date || 'Present'].filter(Boolean).join(' - ');
              jobLine += ` | ${dates}`;
            }
            expSection.push(jobLine);
            if (job.responsibilities && Array.isArray(job.responsibilities)) {
              job.responsibilities.forEach((resp: string) => {
                expSection.push(`• ${resp}`);
              });
            } else if (job.description) {
              expSection.push(`• ${job.description}`);
            }
            expSection.push('');
          });
          sections.push(expSection.join('\n'));
        } else if (resumeJson.Experience && Array.isArray(resumeJson.Experience)) {
          // Legacy format
          const expSection = ['PROFESSIONAL EXPERIENCE'];
          resumeJson.Experience.forEach((exp: any) => {
            expSection.push(`${exp.JobTitle || exp.Title || ''} | ${exp.Company || ''} | ${exp.Location || ''} | ${exp.Dates || ''}`);
            if (exp.Responsibilities && Array.isArray(exp.Responsibilities)) {
              exp.Responsibilities.forEach((resp: string) => {
                expSection.push(`• ${resp}`);
              });
            }
            expSection.push('');
          });
          sections.push(expSection.join('\n'));
        }

        // Education
        if (resumeJson.education && Array.isArray(resumeJson.education)) {
          const eduSection = ['EDUCATION'];
          resumeJson.education.forEach((edu: any) => {
            let eduLine = edu.degree || edu.degree_type || '';
            if (edu.institution || edu.school) eduLine += ` | ${edu.institution || edu.school}`;
            if (edu.location) eduLine += ` | ${edu.location}`;
            if (edu.graduation_date || edu.year) eduLine += ` | ${edu.graduation_date || edu.year}`;
            if (eduLine.trim()) {
              // Only add if there's actual content
              eduSection.push(eduLine);
            }
          });
          if (eduSection.length > 1) {
            // Only add section if there are actual education entries
            sections.push(eduSection.join('\n'));
          }
        } else if (resumeJson.Education && Array.isArray(resumeJson.Education)) {
          // Handles BOTH the legacy { Degree, School, Year } shape AND the
          // VectorShift { College_Degree_Name, Major, College_Name, City, ST,
          // Year_Earned } shape in a single pass.
          const eduSection = ['EDUCATION'];
          resumeJson.Education.forEach((edu: any) => {
            const degree = edu.College_Degree_Name || edu.Degree || '';
            const major = edu.Major || '';
            const school = edu.College_Name || edu.School || edu.Institution || '';
            const cityState = [edu.City, edu.ST || edu.State].filter(Boolean).join(', ') || edu.Location || '';
            const year = edu.Year_Earned || edu.Year || edu.year || '';
            const degreeLabel = major ? `${degree}, ${major}`.trim().replace(/^,\s*/, '') : degree;
            const eduLine = [degreeLabel, school, cityState, year].filter(Boolean).join(' | ');
            if (eduLine.replace(/\|/g, '').trim()) {
              eduSection.push(eduLine);
            }
          });
          if (eduSection.length > 1) {
            sections.push(eduSection.join('\n'));
          }
        }

        // Skills (handles VectorShift's [{Skill: "..."}] shape, plain strings,
        // and grouped category-objects all in one branch).
        if (resumeJson.skills && Array.isArray(resumeJson.skills)) {
          sections.push(`SKILLS\n${resumeJson.skills.join(' • ')}`);
        } else if (resumeJson.Skills) {
          let skillsText = '';
          if (Array.isArray(resumeJson.Skills)) {
            skillsText = resumeJson.Skills.map((skill: any) => {
              if (typeof skill === 'string') return skill;
              if (typeof skill === 'object' && skill !== null) {
                // VectorShift uses { Skill: "..." }. Also tolerate name/skill/title.
                return skill.Skill || skill.skill || skill.name || skill.title || Object.values(skill).filter((v) => typeof v === 'string').join(' ');
              }
              return String(skill ?? '');
            }).filter(Boolean).join(' • ');
          } else if (typeof resumeJson.Skills === 'object' && resumeJson.Skills !== null) {
            const skillsArray = [];
            for (const [category, skills] of Object.entries(resumeJson.Skills)) {
              if (Array.isArray(skills)) {
                skillsArray.push(`${category}: ${skills.map((s: any) => typeof s === 'object' ? (s.Skill || s.skill || s.name || JSON.stringify(s)) : s).join(', ')}`);
              } else {
                skillsArray.push(`${category}: ${typeof skills === 'object' ? JSON.stringify(skills) : skills}`);
              }
            }
            skillsText = skillsArray.join(' • ');
          } else {
            skillsText = resumeJson.Skills.toString();
          }
          if (skillsText.trim()) sections.push(`SKILLS\n${skillsText}`);
        }

        // Certifications & Licenses (VectorShift schema)
        if (resumeJson.CertificationsAndLicenses && Array.isArray(resumeJson.CertificationsAndLicenses)) {
          const certSection = ['CERTIFICATIONS & LICENSES'];
          resumeJson.CertificationsAndLicenses.forEach((c: any) => {
            const name = c?.Certification_or_License_Name || c?.name || '';
            const org = c?.Institution_Organization || c?.institution || '';
            const year = c?.Year || c?.year || '';
            const line = [name, org, year].filter(Boolean).join(' | ');
            if (line.trim()) certSection.push(line);
          });
          if (certSection.length > 1) sections.push(certSection.join('\n'));
        }

        // Awards & Honors (VectorShift schema)
        if (resumeJson.AwardsAndHonors && Array.isArray(resumeJson.AwardsAndHonors) && resumeJson.AwardsAndHonors.length > 0) {
          const awardSection = ['AWARDS & HONORS'];
          resumeJson.AwardsAndHonors.forEach((a: any) => {
            const name = a?.Award_Name || a?.name || '';
            const org = a?.Institution_Organization || a?.institution || '';
            const year = a?.Year || a?.year || '';
            const line = [name, org, year].filter(Boolean).join(' | ');
            if (line.trim()) awardSection.push(line);
          });
          if (awardSection.length > 1) sections.push(awardSection.join('\n'));
        }

        content = sections.filter(Boolean).join('\n\n');
      }
      console.log('WordDocumentViewer: Formatted content length:', content.length);
      return content || 'Document content could not be formatted. Please check the document structure.';
    } catch (error) {
      console.error('WordDocumentViewer: Error formatting content:', error);
      return `Error formatting document content: ${error.message}`;
    }
  };
  // Normalize document JSON from mixed formats
  const normalizeDocumentJson = (input: any): any => {
    if (!input) return null;

    let parsed = input;

    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return null;
      }
    }

    if (parsed?._type === 'String' && typeof parsed?.value === 'string') {
      try {
        parsed = JSON.parse(parsed.value);
      } catch {
        return parsed;
      }
    }

    return parsed;
  };

  // Build reliable editable content so editor never opens blank
  const getEditableContentForTab = (tab: DocumentTab): string => {
    const original = (tab.originalContent || '').trim();

    if (original) {
      if (original.startsWith('{') || original.startsWith('[')) {
        const parsedOriginal = normalizeDocumentJson(original);
        if (parsedOriginal) {
          const formattedFromOriginal = formatDocumentContent(parsedOriginal, tab.documentType);
          if (formattedFromOriginal?.trim()) return formattedFromOriginal;
        }
      }
      return original;
    }

    const normalizedJson = normalizeDocumentJson(tab.resumeJson || tab.documentData || tab.rawJsonData);
    if (normalizedJson) {
      const formatted = formatDocumentContent(normalizedJson, tab.documentType);
      if (formatted?.trim()) return formatted;

      const fallbackText = convertResumeJsonToText(normalizedJson);
      if (fallbackText?.trim()) return fallbackText;

      return JSON.stringify(normalizedJson, null, 2);
    }

    return ''; // Return empty — will trigger docx extraction on edit
  };

  // Extract text from a .docx file URL using JSZip
  const extractTextFromDocx = async (fileUrl: string): Promise<string> => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(`Failed to fetch document: ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);
      const docXml = await zip.file('word/document.xml')?.async('string');
      if (!docXml) return 'Could not read document content.';
      
      // Parse XML and extract text
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(docXml, 'application/xml');
      const textNodes = xmlDoc.getElementsByTagName('w:t');
      const paragraphs: string[] = [];
      let currentParagraph = '';
      
      // Walk through all w:p elements for paragraph structure
      const pElements = xmlDoc.getElementsByTagName('w:p');
      for (let i = 0; i < pElements.length; i++) {
        const p = pElements[i];
        const tNodes = p.getElementsByTagName('w:t');
        let pText = '';
        for (let j = 0; j < tNodes.length; j++) {
          pText += tNodes[j].textContent || '';
        }
        if (pText.trim()) {
          paragraphs.push(pText);
        } else {
          paragraphs.push(''); // preserve blank lines
        }
      }
      
      return paragraphs.join('\n');
    } catch (error) {
      console.error('Error extracting text from docx:', error);
      return 'Failed to extract document text. Please try again.';
    }
  };

  // Handle entering edit mode — extract text from docx if needed
  const handleToggleEditMode = async () => {
    if (isEditingMode) {
      setIsEditingMode(false);
      return;
    }
    
    const tab = tabs.find(t => t.id === activeTab);
    if (!tab) return;
    
    const existingContent = getEditableContentForTab(tab);
    if (existingContent.trim()) {
      setIsEditingMode(true);
      return;
    }
    
    // No content available — try to extract from the Word file
    if (tab.fileUrl) {
      setPreparingDataFor(activeTab);
      try {
        const extracted = await extractTextFromDocx(tab.fileUrl);
        if (extracted.trim()) {
          updateTab(tab.id, { originalContent: extracted });
        }
      } catch (err) {
        console.error('Failed to extract docx text:', err);
      } finally {
        setPreparingDataFor(null);
      }
    }
    
    setIsEditingMode(true);
  };

  // Generate clean filename for downloaded documents (improved)
  const generateCleanFilename = (tab: DocumentTab, format: 'docx' | 'pdf' = 'docx'): string => {
    try {
      // Try to extract meaningful data from document data
      let personName = '';
      let jobTitle = '';
      let companyName = '';
      const docData = tab.documentData || tab.resumeJson || {};

      // First try to get job title and company from session data
      if (tab.tailoringSessionId && tailoredSessions.length > 0) {
        const session = tailoredSessions.find(s => s.session_id === tab.tailoringSessionId);
        if (session) {
          jobTitle = session.job_title || '';
          companyName = session.company_name || '';
        }
      }

      // Extract person name from various sources
      if (docData.PersonalInfo) {
        personName = `${docData.PersonalInfo.firstName || ''} ${docData.PersonalInfo.lastName || ''}`.trim();
      } else if (docData.personal_info) {
        personName = `${docData.personal_info.first_name || ''} ${docData.personal_info.last_name || ''}`.trim();
      } else if (docData.personal_information) {
        personName = docData.personal_information.full_name || '';
      } else if (docData.PersonalInformation) {
        personName = docData.PersonalInformation.FullName || '';
      } else if (docData.Name) {
        personName = docData.Name;
      } else if (docData.header?.name) {
        personName = docData.header.name;
      }

      // If we didn't find job title and company from session, try document data
      if (!jobTitle || !companyName) {
        if (docData.JobApplication) {
          jobTitle = jobTitle || docData.JobApplication.jobTitle || '';
          companyName = companyName || docData.JobApplication.company || '';
        } else if (docData.job_application) {
          jobTitle = jobTitle || docData.job_application.job_title || '';
          companyName = companyName || docData.job_application.company || '';
        }
      }

      // Get document type label
      const docTypeLabel = tab.documentType === 'cover_letter' ? 'Cover Letter' : tab.documentType === 'thank_you_letter' ? 'Thank You Letter' : tab.documentType === 'executive_bio' ? 'Executive Bio' : tab.documentType === 'linkedin_profile' ? 'LinkedIn Profile' : tab.documentType === 'outreach_letter' ? 'Outreach Letter' : 'Resume';

      // Generate current date in same format as creation
      const currentDate = new Date().toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });

      // Build clean filename with proper spacing and date
      let cleanName = '';
      if (personName) {
        cleanName = personName;
        if (jobTitle && companyName) {
          cleanName += ` - ${docTypeLabel} - ${jobTitle} - ${companyName} - ${currentDate}`;
        } else if (companyName) {
          cleanName += ` - ${docTypeLabel} - ${companyName} - ${currentDate}`;
        } else if (jobTitle) {
          cleanName += ` - ${docTypeLabel} - ${jobTitle} - ${currentDate}`;
        } else {
          cleanName += ` - ${docTypeLabel} - ${currentDate}`;
        }
      } else {
        // Fallback: use original filename but remove generated_ prefix and add date
        const originalName = tab.fileName.replace(/^generated_\d+_/, '').replace(/\.[^/.]+$/, "");
        const cleanedOriginal = originalName.replace(/_/g, ' ');
        cleanName = `${cleanedOriginal} - ${currentDate}`;
      }

      // Clean the filename but preserve spaces
      const sanitized = cleanName.replace(/[^a-zA-Z0-9\s\-_.]/g, '').replace(/\s+/g, ' ').trim();
      return `${sanitized}.${format}`;
    } catch (error) {
      console.error('Error generating clean filename:', error);
      // Fallback: clean up original filename
      const fallback = tab.fileName.replace(/^generated_\d+_/, '').replace(/_/g, ' ');
      return fallback.replace(/\.[^/.]+$/, `.${format}`);
    }
  };

  // Convert Word document to PDF
  const convertToPDF = async (tab: DocumentTab): Promise<string | null> => {
    try {
      setIsConverting(true);
      const {
        data,
        error
      } = await supabase.functions.invoke('convert-word-to-pdf', {
        body: {
          fileUrl: tab.fileUrl,
          fileName: tab.fileName
        }
      });
      if (error) throw error;
      if (data && data.success && data.pdfUrl) {
        return data.pdfUrl;
      } else {
        throw new Error(data?.message || 'PDF conversion not available');
      }
    } catch (error: any) {
      console.error('Error converting to PDF:', error);
      toast({
        title: "PDF Conversion Unavailable",
        description: "PDF conversion service is not yet implemented. Downloading Word document instead.",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsConverting(false);
    }
  };

  // Download single document with format options
  const downloadDocument = async (tab: DocumentTab, format: 'docx' | 'pdf' | 'both' = 'docx') => {
    if (!tab.fileUrl) {
      toast({
        title: "Download Failed",
        description: "No file available for download",
        variant: "destructive"
      });
      return;
    }
    setIsDownloading(true);
    try {
      const downloads: Promise<void>[] = [];

      // Download Word document
      if (format === 'docx' || format === 'both') {
        downloads.push((async () => {
          const response = await fetch(tab.fileUrl);
          if (!response.ok) throw new Error('Download failed');
          const blob = await response.blob();
          const cleanFilename = generateCleanFilename(tab, 'docx');
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = cleanFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })());
      }

      // Download PDF document
      if (format === 'pdf' || format === 'both') {
        downloads.push((async () => {
          const pdfUrl = await convertToPDF(tab);
          if (pdfUrl) {
            const response = await fetch(pdfUrl);
            if (!response.ok) throw new Error('PDF download failed');
            const blob = await response.blob();
            const cleanFilename = generateCleanFilename(tab, 'pdf');
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = cleanFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        })());
      }
      await Promise.all(downloads);
      const formatLabel = format === 'both' ? 'Word and PDF' : format === 'pdf' ? 'PDF' : 'Word';
      toast({
        title: "Download Complete",
        description: `Downloaded ${generateCleanFilename(tab)} as ${formatLabel}`
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download the document",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Download all documents individually with format options
  const downloadAllDocuments = async (format: 'docx' | 'pdf' | 'both' = 'docx') => {
    const availableTabs = tabs.filter(tab => tab.fileUrl);
    if (availableTabs.length === 0) {
      toast({
        title: "No Documents Available",
        description: "No documents available for download",
        variant: "destructive"
      });
      return;
    }
    setIsDownloading(true);
    const formatLabel = format === 'both' ? 'Word and PDF' : format === 'pdf' ? 'PDF' : 'Word';
    toast({
      title: "Starting Downloads",
      description: `Downloading ${availableTabs.length} document(s) as ${formatLabel}...`
    });

    // Download each document with a small delay to prevent overwhelming the browser
    for (const [index, tab] of availableTabs.entries()) {
      try {
        await downloadDocument(tab, format);
        // Small delay between downloads
        if (index < availableTabs.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`Failed to download ${tab.fileName}:`, error);
      }
    }
    setIsDownloading(false);
    toast({
      title: "Downloads Complete",
      description: `Downloaded ${availableTabs.length} document(s) as ${formatLabel}`
    });
  };

  // Download all documents as ZIP with format options
  const downloadAsZip = async (format: 'docx' | 'pdf' | 'both' = 'docx') => {
    const availableTabs = tabs.filter(tab => tab.fileUrl);
    if (availableTabs.length === 0) {
      toast({
        title: "No Documents Available",
        description: "No documents available for download",
        variant: "destructive"
      });
      return;
    }
    setIsDownloading(true);
    try {
      const formatLabel = format === 'both' ? 'Word and PDF' : format === 'pdf' ? 'PDF' : 'Word';
      toast({
        title: "Creating ZIP Package",
        description: `Packaging ${availableTabs.length} document(s) as ${formatLabel}...`
      });
      const zip = new JSZip();

      // Create folders for different formats if downloading both
      const wordFolder = format === 'both' ? zip.folder('Word Documents') : zip;
      const pdfFolder = format === 'both' ? zip.folder('PDF Documents') : zip;

      // Download and add each document to ZIP
      for (const tab of availableTabs) {
        try {
          // Add Word document
          if (format === 'docx' || format === 'both') {
            const response = await fetch(tab.fileUrl);
            if (response.ok) {
              const blob = await response.blob();
              const cleanFilename = generateCleanFilename(tab, 'docx');
              if (wordFolder) {
                wordFolder.file(cleanFilename, blob);
              }
            }
          }

          // Add PDF document
          if (format === 'pdf' || format === 'both') {
            const pdfUrl = await convertToPDF(tab);
            if (pdfUrl) {
              const response = await fetch(pdfUrl);
              if (response.ok) {
                const blob = await response.blob();
                const cleanFilename = generateCleanFilename(tab, 'pdf');
                if (pdfFolder) {
                  pdfFolder.file(cleanFilename, blob);
                }
              }
            }
          }
        } catch (error) {
          console.error(`Failed to add ${tab.fileName} to ZIP:`, error);
        }
      }

      // Generate ZIP file
      const zipBlob = await zip.generateAsync({
        type: 'blob'
      });
      const zipFilename = `Documents_${formatLabel.replace(' and ', '_')}_${new Date().toISOString().split('T')[0]}.zip`;

      // Download ZIP
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({
        title: "ZIP Download Complete",
        description: `Downloaded ${zipFilename} with ${availableTabs.length} document(s)`
      });
    } catch (error) {
      console.error('Error creating ZIP:', error);
      toast({
        title: "ZIP Creation Failed",
        description: "Failed to create ZIP package",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-800">
      <div className="min-h-screen flex flex-col">
        {/* Compact Header */}
        <div className="h-12 border-b bg-blue-900 flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-white" />
              <h1 className="text-lg font-semibold text-white">TailorWiz Resume Viewer/Editor</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Always show Edit/View Toggle */}
            
            
            
            
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-h-0">
          {tabs.length === 0 ? (/* Welcome Screen - Simplified */
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-800">
              <div className="max-w-2xl mx-auto text-center space-y-8 px-6">
                <div className="space-y-4 mx-0 my-[32px]">
                  <FileText className="h-20 w-20 mx-auto text-white" />
                  <h1 className="text-4xl font-bold text-white">TailorWiz Resume Viewer/Editor</h1>
                  <p className="text-xl text-blue-100">Instantly Access and Edit Your Tailored Documents</p>
                </div>

                <div className="mt-12">
                  {/* Load from Library */}
                  <Card className="p-8 hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/50">
                    <div className="text-center space-y-4">
                      <div className="h-16 w-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <FolderOpen className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-2xl font-semibold text-gray-900">Open from Tailored Library</h3>
                      <p className="text-gray-600">Quickly load any resume or cover letter you've created in TailorWiz. Everything from previous tailoring sessions is saved for easy access.</p>
                      <Button size="lg" variant="outline" onClick={() => {
                    setIsLibraryOpen(true);
                    loadTailoredSessions();
                  }} disabled={isLoadingSessions} className="mt-4 hover:border-primary rounded bg-gray-300 hover:bg-gray-200">
                        {isLoadingSessions ? <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Loading Library...
                          </> : <>
                          <FolderOpen className="h-5 w-5 mr-2" />
                            Browse Resume Library
                          </>}
                      </Button>
                    </div>
                  </Card>
                  
                  {/* Navigation Links */}
                  <div className="flex justify-center gap-4 mt-8">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-white hover:bg-white/10 hover:text-white border border-white/20 hover:border-white/40">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Dashboard
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/?view=resume-library', {
                  state: {
                    autoSelectTailored: true
                  }
                })} className="text-white hover:bg-white/10 hover:text-white border border-white/20 hover:border-white/40">
                      <FileText className="h-4 w-4 mr-2" />
                      Back to Tailored Documents Library
                    </Button>
                  </div>
                </div>

                {/* Library Dialog */}
                <Dialog open={isLibraryOpen} onOpenChange={setIsLibraryOpen}>
                  <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FolderOpen className="h-5 w-5" />
                        Load from Tailored Documents Library
                      </DialogTitle>
                      <DialogDescription>
                        Select a tailoring session to load all its documents into the editor
                      </DialogDescription>
                    </DialogHeader>

                    {isLoadingSessions ? <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        Loading your tailored documents...
                      </div> : tailoredSessions.length === 0 ? <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No tailored documents found.</p>
                        <p className="text-sm">Create some tailored documents first to see them here.</p>
                      </div> : <div className="grid gap-4">
                          {tailoredSessions.map(session => <Card key={session.session_id} className="group cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-primary/10 border border-border/50 hover:border-primary/30 bg-gradient-to-r from-background to-muted/20" onClick={() => loadSessionDocuments(session)}>
                              <CardContent className="p-6 rounded-md bg-gray-100">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 space-y-3">
                                    {/* Header with persona name and date */}
                                    <div className="flex items-start justify-between">
                                      <div className="space-y-1">
                                        <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                                          {session.user_name || 'Resume Persona'}
                                        </h3>
                                        <div className="flex items-center gap-3 text-muted-foreground">
                                          <span className="font-medium text-foreground">{session.job_title}</span>
                                          <span className="text-muted-foreground">•</span>
                                          <span className="font-medium text-foreground">{session.company_name}</span>
                                        </div>
                                      </div>
                                      <Badge variant="outline" className="shrink-0 border-primary/20 text-primary/80">
                                        <Calendar className="h-3 w-3 mr-1" />
                                        {formatDate(session.created_at)}
                                      </Badge>
                                    </div>

                                    {/* Document types */}
                                    <div className="flex flex-wrap gap-2">
                                      {session.documents.map(doc => <Badge key={doc.id} variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                                          <span className="mr-1">{getDocumentIcon(doc.document_type)}</span>
                                          {doc.document_type === 'resume' ? 'Resume' : doc.document_type === 'cover_letter' ? 'Cover Letter' : doc.document_type === 'thank_you_letter' ? 'Thank You' : doc.document_type === 'executive_bio' ? 'Executive Bio' : doc.document_type === 'linkedin_profile' ? 'LinkedIn' : doc.document_type === 'outreach_letter' ? 'Outreach' : doc.document_type}
                                        </Badge>)}
                                    </div>

                                    {/* Match score if available */}
                                    {session.resume_document?.match_score && <div className="flex items-center gap-4 pt-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-muted-foreground">Match Score:</span>
                                          <Badge variant={session.resume_document.match_score >= 80 ? "default" : "secondary"} className="font-bold">
                                            {session.resume_document.match_score}%
                                          </Badge>
                                        </div>
                                        {session.resume_document.original_match_score && <div className="flex items-center gap-1">
                                            <span className="text-sm text-green-600 font-medium">
                                              +{session.resume_document.match_score - session.resume_document.original_match_score}% improvement
                                            </span>
                                          </div>}
                                      </div>}
                                  </div>
                                  
                                  {/* Action indicator */}
                                  <div className="flex items-center text-muted-foreground group-hover:text-primary transition-colors ml-4">
                                    <div className="text-center">
                                      <div className="text-xs font-medium mb-1">{session.documents.length} docs</div>
                                      <ArrowLeft className="h-5 w-5 rotate-180 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>)}
                        </div>}
                  </DialogContent>
                </Dialog>
              </div>
            </div>) : (/* Existing Tabbed Interface */
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              {/* ... keep existing code */}
              {/* Action Bar Above Tabs */}
              <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button size="sm" variant="outline" onClick={() => navigate('/?view=resume-library', {
                state: {
                  autoSelectTailored: true
                }
              })} className="bg-white border-gray-300 hover:bg-slate-50 hover:border-slate-400 shadow-sm mx-[10px]">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Tailored Library
                  </Button>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Template switcher — re-renders every open tab in the chosen design.
                      Mirrors the dropdown on the Tailored Documents results page. */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2 bg-purple-50 border-2 border-purple-500 text-purple-900 hover:bg-purple-100 hover:border-purple-600 font-semibold shadow-sm"
                        disabled={isGenerating}
                      >
                        {isGenerating ? (
                          <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-purple-600" />
                        )}
                        <span className="text-xs font-normal text-purple-700">Template:</span>
                        <span className="font-semibold">{docmosisTemplateLabel(activeTemplateId)}</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 bg-white z-50 max-h-96 overflow-y-auto p-1">
                      <div className="px-3 py-2 mb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b">
                        Switch Resume Template
                      </div>
                      {DOCMOSIS_TEMPLATE_OPTIONS.filter((opt) => opt.id !== 'original-format' || !!originalDocxUrl).map((opt) => {
                        const isSelected = activeTemplateId === opt.id;
                        return (
                          <DropdownMenuItem
                            key={opt.id}
                            onClick={() => switchTemplateForAllTabs(opt.id)}
                            disabled={isGenerating}
                            className={
                              isSelected
                                ? 'bg-purple-100 border-l-4 border-purple-600 my-1 rounded-r focus:bg-purple-100'
                                : 'border-l-4 border-transparent my-1'
                            }
                          >
                            <FileText className={`h-4 w-4 mr-2 ${isSelected ? 'text-purple-700' : 'text-muted-foreground'}`} />
                            <div className="flex flex-col flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${isSelected ? 'text-purple-900' : ''}`}>{opt.label}</span>
                                {isSelected && (
                                  <Badge className="bg-purple-600 hover:bg-purple-600 text-white text-[10px] px-1.5 py-0 h-4 font-bold">
                                    CURRENT
                                  </Badge>
                                )}
                              </div>
                              <span className={`text-xs ${isSelected ? 'text-purple-700' : 'text-muted-foreground'}`}>{opt.subtitle}</span>
                            </div>
                            {isSelected && (
                              <CheckCircle className="h-4 w-4 ml-2 text-purple-600 flex-shrink-0" />
                            )}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Load from Library */}
                  <Dialog open={isLibraryOpen} onOpenChange={setIsLibraryOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" onClick={() => {
                    setIsLibraryOpen(true);
                    loadTailoredSessions();
                  }} className="bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-400 shadow-sm">
                        <FolderOpen className="h-4 w-4 mr-2" />
                        Load Resume from Library
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <FolderOpen className="h-5 w-5" />
                          Load from Tailored Documents Library
                        </DialogTitle>
                        <DialogDescription>
                          Select a tailoring session to load all its documents into the editor
                        </DialogDescription>
                      </DialogHeader>

                      {isLoadingSessions ? <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin mr-2" />
                          Loading your tailored documents...
                        </div> : tailoredSessions.length === 0 ? <div className="text-center py-8 text-gray-500">
                          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p>No tailored documents found.</p>
                          <p className="text-sm">Create some tailored documents first to see them here.</p>
                        </div> : <div className="space-y-4">
                          {tailoredSessions.map(session => <Card key={session.session_id} className="p-4 hover:shadow-md transition-shadow cursor-pointer border border-gray-200 hover:border-blue-300" onClick={() => loadSessionDocuments(session)}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h3 className="font-semibold text-lg text-gray-900">
                                      {session.job_title} - {session.company_name}
                                    </h3>
                                    <Badge variant="outline" className="text-xs">
                                      <Calendar className="h-3 w-3 mr-1" />
                                      {formatDate(session.created_at)}
                                    </Badge>
                                  </div>
                                  
                                  <div className="flex flex-wrap gap-2 mb-3">
                                    {session.documents.map(doc => <Badge key={doc.id} variant="secondary" className="text-xs flex items-center gap-1">
                                        <span>{getDocumentIcon(doc.document_type)}</span>
                                        {doc.document_type === 'resume' ? 'Resume' : doc.document_type === 'cover_letter' ? 'Cover Letter' : doc.document_type === 'thank_you_letter' ? 'Thank You Letter' : doc.document_type === 'executive_bio' ? 'Executive Bio' : doc.document_type === 'linkedin_profile' ? 'LinkedIn Profile' : doc.document_type === 'outreach_letter' ? 'Outreach Letter' : doc.document_type}
                                      </Badge>)}
                                  </div>

                                  {session.resume_document?.match_score && <div className="flex items-center gap-4 text-sm text-gray-600">
                                      <div className="flex items-center gap-1">
                                        <span className="font-medium">Match Score:</span>
                                        <Badge variant={session.resume_document.match_score >= 80 ? "default" : "secondary"}>
                                          {session.resume_document.match_score}%
                                        </Badge>
                                      </div>
                                      {session.resume_document.original_match_score && <div className="flex items-center gap-1">
                                          <span className="text-green-600 font-medium">
                                            +{session.resume_document.match_score - session.resume_document.original_match_score}% improvement
                                          </span>
                                        </div>}
                                    </div>}
                                </div>
                                
                                <div className="flex items-center text-gray-400">
                                  <span className="text-sm mr-2">{session.documents.length} docs</span>
                                  <ArrowLeft className="h-4 w-4 rotate-180" />
                                </div>
                              </div>
                            </Card>)}
                        </div>}
                    </DialogContent>
                  </Dialog>

                  {/* Tailor this Resume Again button */}
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      const activeTabData = activeTab && tabs.find(tab => tab.id === activeTab);
                      
                      if (activeTabData) {
                        // Use stored plain text content instead of converting JSON
                        let resumeText = null;
                        
                        if (activeTabData.originalContent) {
                          // Use the stored plain text content from the database
                          resumeText = activeTabData.originalContent;
                        } else {
                          // Fallback: convert JSON to text if no plain text available
                          let resumeJson = null;
                          if (activeTabData.resumeJson) {
                            resumeJson = activeTabData.resumeJson;
                          } else if (activeTabData.documentData) {
                            resumeJson = activeTabData.documentData;
                          } else if (activeTabData.rawJsonData) {
                            resumeJson = activeTabData.rawJsonData;
                          }
                          
                          if (resumeJson) {
                            resumeText = convertResumeJsonToText(resumeJson);
                          }
                        }
                        
                        if (resumeText) {
                          navigate('/resume-tailoring-fixed-duplicate', {
                            state: { 
                              processedResumeData: resumeText,
                              step1Complete: true,
                              basicOption: 'upload',
                              fileName: activeTabData.fileName
                            }
                          });
                        } else {
                          toast({
                            title: "No Resume Data",
                            description: "No resume data found for this document",
                            variant: "destructive"
                          });
                        }
                      } else {
                        toast({
                          title: "No Document Selected",
                          description: "Please select a document first",
                          variant: "destructive"
                        });
                      }
                    }}
                    className="bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-400 shadow-sm"
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Tailor this Resume Again
                  </Button>

                  {/* Always show all controls */}
                  <div className="flex items-center gap-2 border-l border-gray-300 pl-2">
                    {/* Unified Edit Document toggle */}
                    <Button 
                      size="sm" 
                      variant={isEditingMode ? "default" : "outline"} 
                      onClick={handleToggleEditMode} 
                      disabled={!activeTab || preparingDataFor === activeTab}
                      className={isEditingMode ? "bg-purple-600 hover:bg-purple-700 text-white" : "bg-white border-gray-300 hover:bg-purple-50 hover:border-purple-400 shadow-sm"}
                    >
                      {isEditingMode ? (
                        <><Eye className="h-4 w-4 mr-2" /> Exit Editor</>
                      ) : (
                        <><Edit3 className="h-4 w-4 mr-2" /> Edit Document</>
                      )}
                    </Button>
                  </div>

                  {/* Enhanced Download Options */}
                  {tabs.length > 1 ? <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="bg-white border-gray-300 hover:bg-green-50 hover:border-green-400 shadow-sm">
                          <Download className="h-4 w-4 mr-2" />
                          Download Options
                          <ChevronDown className="h-3 w-3 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64">
                        <DropdownMenuItem className="font-medium text-sm text-muted-foreground px-2 py-1" disabled>
                          Download This Document
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                    const currentTab = activeTab && tabs.find(tab => tab.id === activeTab);
                    if (currentTab) downloadDocument(currentTab, 'docx');
                  }}>
                          <FileText className="h-4 w-4 mr-2" />
                          As Word Document
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                    const currentTab = activeTab && tabs.find(tab => tab.id === activeTab);
                    if (currentTab) downloadDocument(currentTab, 'pdf');
                  }}>
                          <FileType className="h-4 w-4 mr-2" />
                          As PDF Document
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                    const currentTab = activeTab && tabs.find(tab => tab.id === activeTab);
                    if (currentTab) downloadDocument(currentTab, 'both');
                  }}>
                          <FileDown className="h-4 w-4 mr-2" />
                          As Both (Word + PDF)
                        </DropdownMenuItem>
                        <div className="border-t my-1"></div>
                        <DropdownMenuItem className="font-medium text-sm text-muted-foreground px-2 py-1" disabled>
                          Download All Documents
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => downloadAllDocuments('docx')}>
                          <Package className="h-4 w-4 mr-2" />
                          All as Word (Individual)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => downloadAllDocuments('pdf')}>
                          <Package className="h-4 w-4 mr-2" />
                          All as PDF (Individual)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => downloadAllDocuments('both')}>
                          <Package className="h-4 w-4 mr-2" />
                          All as Both (Individual)
                        </DropdownMenuItem>
                        <div className="border-t my-1"></div>
                        <DropdownMenuItem onClick={() => downloadAsZip('docx')}>
                          <Package className="h-4 w-4 mr-2" />
                          ZIP Package (Word)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => downloadAsZip('pdf')}>
                          <Package className="h-4 w-4 mr-2" />
                          ZIP Package (PDF)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => downloadAsZip('both')}>
                          <Package className="h-4 w-4 mr-2" />
                          ZIP Package (Both)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu> : <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" disabled={isDownloading || isConverting} className="bg-white border-gray-300 hover:bg-green-50 hover:border-green-400 shadow-sm">
                          <Download className="h-4 w-4 mr-2" />
                          {isDownloading ? 'Downloading...' : 'Download Options'}
                          <ChevronDown className="h-3 w-3 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => {
                    const currentTab = activeTab && tabs.find(tab => tab.id === activeTab);
                    if (currentTab) downloadDocument(currentTab, 'docx');
                  }}>
                          <FileText className="h-4 w-4 mr-2" />
                          As Word Document
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                    const currentTab = activeTab && tabs.find(tab => tab.id === activeTab);
                    if (currentTab) downloadDocument(currentTab, 'pdf');
                  }}>
                          <FileType className="h-4 w-4 mr-2" />
                          As PDF Document
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                    const currentTab = activeTab && tabs.find(tab => tab.id === activeTab);
                    if (currentTab) downloadDocument(currentTab, 'both');
                  }}>
                          <FileDown className="h-4 w-4 mr-2" />
                          As Both (Word + PDF)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>}
                </div>
              </div>

              {/* Modern Tab Header */}
              <div className="bg-background border-b border-border flex items-end px-6 flex-shrink-0">
                <div className="flex items-end -mb-px">
                  {tabs.map((tab, index) => <div key={tab.id} className={`relative group transition-all duration-200 mr-1`}>
                      <button className={`
                          relative flex items-center transition-all duration-200 rounded-t-lg
                          ${activeTab === tab.id ? 'px-6 py-4 font-semibold text-white bg-blue-900 border-x border-t border-blue-900 shadow-sm z-10' : 'px-4 py-3 font-medium text-muted-foreground bg-muted/30 border border-b-0 border-blue-900'}
                        `} onClick={() => setActiveTab(tab.id)}>
                        {/* Tab Number Badge */}
                        <div className={`w-5 h-5 rounded-full text-xs flex items-center justify-center font-semibold mr-3 flex-shrink-0 ${activeTab === tab.id ? 'bg-primary-foreground text-primary' : 'bg-muted-foreground text-background group-hover:bg-foreground'}`}>
                          {index + 1}
                        </div>
                        
                        <FileText className="w-4 h-4 mr-2 flex-shrink-0" />
                        
                        <span className="truncate max-w-32 min-w-0">
                          {tab.fileName.replace(/\.[^/.]+$/, "")}
                        </span>
                        
                        {/* Edit Mode Indicator */}
                        {tab.isEditing && <Edit3 className="w-3 h-3 ml-2 flex-shrink-0 text-accent" />}
                        
                        {/* Close Button */}
                        <button className={`ml-2 h-4 w-4 p-0 flex-shrink-0 rounded-full flex items-center justify-center transition-colors ${activeTab === tab.id ? 'hover:bg-primary-foreground/20 text-primary-foreground/70 hover:text-destructive' : 'hover:bg-destructive/10 hover:text-destructive text-muted-foreground'}`} onClick={e => {
                    e.stopPropagation();
                    closeTab(tab.id);
                  }}>
                          <X className="h-3 w-3" />
                        </button>
                      </button>
                    </div>)}
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 bg-gray-50 min-h-0">
                {tabs.map(tab => (
                  <div key={tab.id} className={`h-full ${activeTab === tab.id ? 'block' : 'hidden'}`}>
                    {/* Unified Editing Mode */}
                    {isEditingMode && activeTab === tab.id ? (
                      <DocumentEditingView
                        documentContent={getEditableContentForTab(tab)}
                        documentJson={normalizeDocumentJson(tab.resumeJson || tab.documentData || tab.rawJsonData)}
                        documentType={tab.documentType}
                        fileUrl={tab.fileUrl}
                        fileName={tab.fileName || tab.id}
                        // Map the chosen Docmosis template to its design family
                        // so the WYSIWYG editor uses the matching font / accent.
                        templateFamily={
                          activeTemplateId === 'template1' ? 'classic' :
                          activeTemplateId === 'template2' ? 'navy-serif' :
                          activeTemplateId === 'template3' ? 'professional' :
                          activeTemplateId === 'template4' || activeTemplateId === 'template5' ? 'burgundy' :
                          activeTemplateId === 'template6' ? 'charcoal-gold' :
                          activeTemplateId === 'template7' ? 'minimal' :
                          (activeTemplateId === 'template8' || activeTemplateId === 'template9') ? 'slate-sans' :
                          'professional'
                        }
                        docmosisTemplateId={activeTemplateId}
                        jobDescription={(location.state as any)?.jobDescription}
                        onContentUpdate={(newContent) => {
                          updateTab(tab.id, { originalContent: newContent });
                        }}
                        onJsonUpdate={(newJson) => {
                          updateTab(tab.id, { resumeJson: newJson, documentData: newJson });
                        }}
                        onSaveAndRegenerate={async () => {
                          const jsonData = normalizeDocumentJson(tab.resumeJson || tab.documentData || tab.rawJsonData);
                          if (!jsonData) return;
                          await regenerateAndAttach(tab.id, tab.documentType, jsonData);
                        }}
                        onSaveAndRegenerateWithJson={async (newJson: any) => {
                          // Caller (editor) hands us the most-recent JSON
                          // built from the user's edits, so we don't lean on
                          // potentially-stale `tab.resumeJson`.
                          const jsonData = normalizeDocumentJson(newJson);
                          if (!jsonData) return;
                          await regenerateAndAttach(tab.id, tab.documentType, jsonData);
                        }}
                        onSmartFormat={
                          // Resume-only: re-render through the page-fit engine
                          // (keeps the first job on page 1). Letters / LinkedIn
                          // docs keep the plain text-edit behaviour.
                          (isLetterDocumentType(normalizeDocumentType(tab.documentType)) ||
                            isLinkedDocumentType(normalizeDocumentType(tab.documentType)))
                            ? undefined
                            : async () => {
                                const jsonData = normalizeDocumentJson(tab.resumeJson || tab.documentData || tab.rawJsonData);
                                if (!jsonData) throw new Error('No resume data available to format');

                                const { data: wordData, error: wordError } = await supabase.functions.invoke('generate-bulk-resumes', {
                                  body: { ...jsonData, templateId: activeTemplateId, ...designExtra(activeTemplateId) }
                                });
                                if (wordError) throw wordError;
                                if (!wordData?.success) throw new Error('Failed to reformat document');

                                // The fit engine returns the (possibly condensed) resume JSON.
                                const fittedJson = wordData.resumeJson || jsonData;

                                // Upload the freshly rendered .docx and refresh the preview.
                                const binaryString = atob(wordData.files[0].data);
                                const bytes = new Uint8Array(binaryString.length);
                                for (let i = 0; i < binaryString.length; i++) {
                                  bytes[i] = binaryString.charCodeAt(i);
                                }
                                const blob = new Blob([bytes], {
                                  type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                });
                                const safeFileName = (wordData.files[0].filename || 'resume.docx').replace(/[^a-zA-Z0-9._-]/g, '_');
                                const storagePath = `generated_${Date.now()}_${safeFileName}`;
                                const { error: uploadError } = await supabase.storage.from('word-documents').upload(storagePath, blob, {
                                  cacheControl: '3600',
                                  upsert: false
                                });
                                if (uploadError) throw uploadError;
                                const { data: urlData } = supabase.storage.from('word-documents').getPublicUrl(storagePath);

                                // Sync editor state with whatever the fit loop changed.
                                updateTab(tab.id, {
                                  resumeJson: fittedJson,
                                  documentData: fittedJson,
                                  originalContent: undefined,
                                  fileUrl: urlData?.publicUrl || tab.fileUrl,
                                });
                              }
                        }
                        onClose={() => setIsEditingMode(false)}
                      />
                    ) : tab.fileUrl ? (
                      /* Word Document Viewer Mode */
                      <div className="h-full p-6 overflow-auto">
                        <div style={{
                          width: '100%',
                          height: 'calc(100vh - 200px)',
                          minHeight: '600px'
                        }}>
                          <WordOnlineViewer 
                            documentUrl={tab.fileUrl} 
                            fileName={tab.fileName} 
                            className="w-full h-full border-0 outline-none focus:outline-none" 
                            height="100%" 
                          />
                        </div>
                      </div>
                    ) : (
                      /* Fallback: document still generating or generation failed */
                      <div className="h-full flex items-center justify-center bg-muted/20">
                        <div className="text-center space-y-4 max-w-md">
                          <Loader2 className="h-10 w-10 mx-auto animate-spin text-muted-foreground" />
                          <h3 className="text-lg font-semibold text-foreground">Generating Document…</h3>
                          <p className="text-sm text-muted-foreground">
                            Your {getDocumentTypeLabel(tab.documentType)} is being generated. If this takes too long, try editing and saving the document.
                          </p>
                          <Button size="sm" variant="outline" onClick={() => setIsEditingMode(true)}>
                            <Edit3 className="h-4 w-4 mr-2" /> Open Editor
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Tabs>)}
        </div>
      </div>
    </div>
  );
};
export default WordDocumentViewer;